/**
 * Mount target helpers — a single source of truth shared between the chip,
 * the picker, the configure-asset dialog, and the catalog table.
 *
 * A mount target is exactly the wire shape the backend ``MountTarget`` model
 * expects:
 *
 *   { scope: 'ORG' }
 *   { scope: 'NETWORK', networkId }
 *   { scope: 'POOL',    networkId, poolId }
 *   { scope: 'AGENT',   networkId, agentName }
 *   { scope: 'AGENT_POOL', networkId, agentName, poolId }
 *
 * We never invent a new shape on the client — the backend ``_validate_target``
 * is authoritative, and the helpers here mirror it.
 */

export const SCOPES = Object.freeze({
  ORG: 'ORG',
  NETWORK: 'NETWORK',
  POOL: 'POOL',
  AGENT: 'AGENT',
  AGENT_POOL: 'AGENT_POOL',
});

/** ``suggestTargets`` mode: Org / Network / Agent options (tenant + network tabs). */
export const MOUNT_SUGGEST_FULL = 'full';

/** Agentic pool **owners**: only ``@Pool`` + ``@AgentPool`` (no ``@Network`` / ``@Agent`` / ``@Org``). */
export const MOUNT_SUGGEST_POOL_AXIS = 'pool_axis';

/** Agentic **supervisors**: only ``@AgentPool`` for agents they supervise (``agentNames`` is that subset). */
export const MOUNT_SUGGEST_AGENT_POOL_ONLY = 'agent_pool_only';

/**
 * Normalize optional supervisor whitelist (agent logical ids).
 * ``undefined`` means pool-owner tier (POOL + AgentPool). An array (possibly empty)
 * means supervisor tier (AgentPool only for those agents).
 *
 * @param {string[]|undefined} supervisorAgentIds
 * @returns {string[] | undefined}
 */
export function normalizeAgenticSupervisorIds(supervisorAgentIds) {
  if (supervisorAgentIds === undefined) return undefined;
  if (!Array.isArray(supervisorAgentIds)) return [];
  const out = supervisorAgentIds
    .map((s) => String(s || '').trim())
    .filter((s) => s.length > 0);
  return [...new Set(out)];
}

/**
 * Render a target as a deterministic ``"@"``-prefixed slug, e.g.
 * ``@Network:mount-pika`` / ``@Agent:mount-pika:LeadAgent`` /
 * ``@AgentPool:mount-pika:LeadAgent:acme`` (PAR with ``@Pool:network:pool``).
 * Used for the chip label, autocomplete typeahead, and as the React ``key``
 * for a list of mounts (so identical targets dedupe cleanly).
 *
 * @param {object} target
 * @returns {string}
 */
export function targetLabel(target) {
  if (!target || !target.scope) return '@?';
  switch (target.scope) {
    case SCOPES.ORG:
      // UI label only — the backend scope stays ``ORG``. "Tenant" is clearer
      // (tenant-wide, above any single network) and avoids the "is Org
      // cross-tenant?" ambiguity.
      return '@Tenant';
    case SCOPES.NETWORK:
      return `@Network:${target.networkId || '?'}`;
    case SCOPES.POOL:
      return `@Pool:${target.networkId || '?'}:${target.poolId || '?'}`;
    case SCOPES.AGENT:
      return `@Agent:${target.networkId || '?'}:${target.agentName || '?'}`;
    case SCOPES.AGENT_POOL:
      return `@AgentPool:${target.networkId || '?'}:${target.agentName || '?'}:${target.poolId || '?'}`;
    default:
      return `@${target.scope}`;
  }
}

/**
 * Dedupe key — two mounts with identical scope + ids collide. Network is
 * always lowercased on the server; we keep the original casing for agents
 * and pools (those are slugs but the backend stores them verbatim).
 */
export function targetKey(target) {
  return targetLabel(target);
}

/**
 * Tailwind palette by scope — Slack-style coloured chips. Keep the colour
 * table here so the picker and the table render the same chip.
 */
export function targetClassName(target) {
  switch (target?.scope) {
    case SCOPES.ORG:
      return 'bg-amber-50 text-amber-800 ring-amber-200';
    case SCOPES.NETWORK:
      return 'bg-blue-50 text-blue-800 ring-blue-200';
    case SCOPES.POOL:
      return 'bg-emerald-50 text-emerald-800 ring-emerald-200';
    case SCOPES.AGENT:
      return 'bg-violet-50 text-violet-800 ring-violet-200';
    case SCOPES.AGENT_POOL:
      return 'bg-fuchsia-50 text-fuchsia-800 ring-fuchsia-200';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200';
  }
}

/**
 * Mount-target pyramid coverage.
 *
 *   Org → Network → Pool → AgentPool
 *   Org → Network → Agent → AgentPool
 *
 * ``parent`` *covers* ``child`` when subscribing the asset at ``parent`` already
 * delivers it to the runtime context for ``child``. Tagging both creates a
 * duplicate mount on the same logical scope, which is what we reject so the
 * agent context is built from a unique tag set.
 *
 * @param {object} parent
 * @param {object} child
 * @returns {boolean}
 */
export function targetCovers(parent, child) {
  if (!parent?.scope || !child?.scope) return false;
  const p = String(parent.scope).toUpperCase();
  const c = String(child.scope).toUpperCase();
  if (p === SCOPES.ORG) return c !== SCOPES.ORG;
  if (p === c) return false;
  const pn = String(parent.networkId || '').trim();
  const cn = String(child.networkId || '').trim();
  if (!pn || pn !== cn) return false;
  if (p === SCOPES.NETWORK) {
    return c === SCOPES.AGENT || c === SCOPES.POOL || c === SCOPES.AGENT_POOL;
  }
  if (p === SCOPES.POOL) {
    if (c !== SCOPES.AGENT_POOL) return false;
    return String(parent.poolId || '').trim() === String(child.poolId || '').trim();
  }
  if (p === SCOPES.AGENT) {
    if (c !== SCOPES.AGENT_POOL) return false;
    return String(parent.agentName || '').trim() === String(child.agentName || '').trim();
  }
  return false;
}

/**
 * @param {object} child Candidate target.
 * @param {object[]} existing Mounts or chips already on the asset (target or {target}).
 * @returns {object|null} Existing target that supersedes ``child``, or ``null``.
 */
export function findCoveringMount(child, existing = []) {
  if (!child || !Array.isArray(existing)) return null;
  for (const e of existing) {
    const t = e?.target ?? e;
    if (t && targetCovers(t, child)) return t;
  }
  return null;
}

/**
 * @param {object} parent Newly added target.
 * @param {object[]} existing Current chips/mounts.
 * @returns {object[]} Subset of existing rows that ``parent`` would supersede.
 */
export function findCoveredMounts(parent, existing = []) {
  if (!parent || !Array.isArray(existing)) return [];
  return existing.filter((e) => {
    const t = e?.target ?? e;
    return t && targetCovers(parent, t);
  });
}

/**
 * Reject a duplicate, structurally-malformed, or hierarchy-redundant target
 * before sending it to the server. Returns ``null`` when the target is fine,
 * otherwise an English error message ready to surface in the dialog.
 *
 * @param {object} target
 * @param {object[]} existing
 */
export function validateTarget(target, existing = []) {
  if (!target || !target.scope) return 'Pick a scope (Org / Network / Pool / Agent).';
  if (target.scope === SCOPES.NETWORK && !target.networkId) {
    return 'Network is required for a Network mount.';
  }
  if (target.scope === SCOPES.POOL && (!target.networkId || !target.poolId)) {
    return 'Network and pool are both required for a Pool mount.';
  }
  if (target.scope === SCOPES.AGENT && (!target.networkId || !target.agentName)) {
    return 'Network and agent are both required for an Agent mount.';
  }
  if (
    target.scope === SCOPES.AGENT_POOL
    && (!target.networkId || !target.agentName || !target.poolId)
  ) {
    return 'Network, agent and pool are all required for an Agent+Pool mount.';
  }
  const k = targetKey(target);
  if ((existing || []).some((t) => targetKey(t?.target ?? t) === k)) {
    return `${k} is already attached.`;
  }
  const cover = findCoveringMount(target, existing);
  if (cover) {
    return `Already covered by ${targetKey(cover)} — remove the higher tag first or skip this one.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Network catalog scope — tenant lists all blobs; network tab lists only
// assets with at least one mount that applies here (ORG + this network).
// ---------------------------------------------------------------------------

/**
 * @param {object} mount Row from ``GET …/mounts`` or embedded ``asset.mounts[]``.
 */
export function mountRowToTarget(mount) {
  if (!mount || typeof mount !== 'object') return null;
  const t = mount.target ?? mount;
  return t && typeof t === 'object' && t.scope ? t : null;
}

/** A mount's usage lane, uppercased. Looks on the row and the nested target. */
export function mountUsageType(mount) {
  return String(
    mount?.usageType ?? mount?.usage_type ?? mount?.target?.usageType ?? '',
  ).toUpperCase();
}

/**
 * Is an INHERITED (ancestor) mount worth showing downstream as display-only?
 *
 * Only when it's ACTIVE (RULES / REFERENCE). A held (OFF) ancestor shapes NOTHING
 * in the descendant's context, so surfacing it is just noise — the whole point of
 * the read-only inherited row is "here's what's affecting your context." We only
 * hide on an EXPLICIT ``OFF``; a missing usageType is treated as active so a data
 * gap never silently drops real context. (Own-scope held files are unaffected —
 * they stay visible so you can manage/reactivate them; this gate is ancestors
 * only.) Added 2026-06-21.
 */
export function inheritedMountIsActive(mount) {
  return mountUsageType(mount) !== 'OFF';
}

// ---------------------------------------------------------------------------
// Agentic pool Data Plane — only ``@Pool`` / ``@AgentPool`` (pyramid scoped).
// ---------------------------------------------------------------------------

/**
 * @param {object} mount
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ supervisorAgentIds?: string[] }} [opts]
 */
export function mountVisibleInAgenticPoolContext(mount, networkId, poolId, opts = {}) {
  const t = mountRowToTarget(mount);
  if (!t) return false;
  const nid = String(networkId || '').trim();
  const pid = String(poolId || '').trim();
  if (!nid || !pid) return false;
  const sup = normalizeAgenticSupervisorIds(opts.supervisorAgentIds);
  const sc = String(t.scope || '').toUpperCase();

  // ANCESTORS flow DOWN as display-only (see §5: inherited = visible, read-only).
  // A room's ancestors are its NETWORK and the TENANT(ORG). They are shown so the
  // user sees the full context reaching the room's agents, but are NOT editable
  // here (managed at the scope they were applied) — enforced by
  // ``mountsRemovableAgenticPool`` excluding them. Gated on ``includeAncestors``
  // (default true) so callers that need ONLY the room's own mounts can opt out.
  const includeAncestors = opts.includeAncestors !== false;
  if (includeAncestors) {
    // Inherited ancestors flow down ONLY when ACTIVE — a held (OFF) ORG/NETWORK
    // mount shapes nothing in this room's context, so it's noise here. (The
    // room's own POOL/AGENT_POOL mounts below are unaffected by this gate.)
    if (sc === 'ORG') return inheritedMountIsActive(mount);
    if (sc === 'NETWORK') {
      return String(t.networkId || '').trim() === nid && inheritedMountIsActive(mount);
    }
  }

  // OWN (editable) scopes are pool-axis and must match this network.
  if (String(t.networkId || '').trim() !== nid) return false;
  /** Pool-owner tier — ``@Pool`` + ``@AgentPool`` for this pool axis. */
  if (sup === undefined) {
    if (sc === 'POOL') return String(t.poolId || '').trim() === pid;
    if (sc === 'AGENT_POOL') return String(t.poolId || '').trim() === pid;
    return false;
  }
  /** Supervisor tier — AgentPool mounts they supervise only. */
  if (!sup.length) return false;
  if (sc !== 'AGENT_POOL') return false;
  if (String(t.poolId || '').trim() !== pid) return false;
  const an = String(t.agentName || '').trim();
  return an.length > 0 && sup.includes(an);
}

/**
 * Tenant row appears in Agentic Network Data Plane iff it has ≥1 Agentic-pool-axis mount for this scope.
 *
 * @param {object} asset
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ supervisorAgentIds?: string[] }} [opts]
 */
export function assetHasMountForAgenticPool(asset, networkId, poolId, opts = {}) {
  const mounts = Array.isArray(asset?.mounts) ? asset.mounts : [];
  return mounts.some((m) =>
    mountVisibleInAgenticPoolContext(m, networkId, poolId, opts),
  );
}

/**
 * @param {unknown} assetOrMounts
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ supervisorAgentIds?: string[] }} [opts]
 */
export function mountsVisibleAgenticPool(assetOrMounts, networkId, poolId, opts = {}) {
  const mounts = Array.isArray(assetOrMounts)
    ? assetOrMounts
    : Array.isArray(assetOrMounts?.mounts)
      ? assetOrMounts.mounts
      : [];
  const nid = String(networkId || '').trim();
  const pid = String(poolId || '').trim();
  if (!nid || !pid) return mounts;
  return mounts.filter((m) =>
    mountVisibleInAgenticPoolContext(m, nid, pid, opts),
  );
}

/** Mounts removable from Hold / strip flows in Agentic DP — every visible mount
 *  EXCEPT ORG. ORG is the tenant-wide store-once row managed at tenant level; a
 *  room/network can manage any other tag it can see (incl. inherited NETWORK). */
export function mountsRemovableAgenticPool(assetOrMounts, networkId, poolId, opts = {}) {
  return mountsVisibleAgenticPool(assetOrMounts, networkId, poolId, opts).filter((m) => {
    const sc = String(m?.target?.scope ?? m?.scope ?? '').toUpperCase();
    return sc && sc !== 'ORG';
  });
}

/** @returns {'RULES' | 'REFERENCE' | 'OFF'} */
export function effectiveAssetLaneUsageForAgenticPool(
  asset,
  lane,
  networkId,
  poolId,
  opts = {},
) {
  const want = lane === 'reference' ? 'REFERENCE' : 'RULES';
  // ACTIVE if any visible mount (own OR active inherited ancestor) has this lane.
  // ``mountsVisibleAgenticPool`` already drops held ancestors, so an active @Org/
  // @Network file shows its real lane in the room.
  const vis = mountsVisibleAgenticPool(asset, networkId, poolId, opts);
  for (const m of vis) {
    const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
    if (u === want) return want;
  }
  return 'OFF';
}

/** Aggregate usage scoped to Agentic DP mounts only. */
export function effectiveAssetUsageForAgenticPool(asset, networkId, poolId, opts = {}) {
  const nid = String(networkId || '').trim();
  const pid = String(poolId || '').trim();
  const vis = mountsVisibleAgenticPool(asset, nid, pid, opts);
  const rows = vis;
  if (!rows.length) {
    const d = String(asset?.usageType ?? 'OFF').toUpperCase();
    if (d === 'RULES' || d === 'REFERENCE' || d === 'OFF') return d;
    return 'OFF';
  }
  const usages = [];
  for (const m of rows) {
    const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
    if (u === 'RULES' || u === 'REFERENCE' || u === 'OFF') usages.push(u);
  }
  const active = usages.filter((u) => u !== 'OFF');
  if (active.length === 0) return 'OFF';
  const uniq = [...new Set(active)];
  if (uniq.length === 1) return uniq[0];
  if (uniq.includes('RULES')) return 'RULES';
  return uniq.includes('REFERENCE') ? 'REFERENCE' : 'OFF';
}

/**
 * Does a mount target belong in the NETWORK workbench view?
 *
 * Context flows DOWN the pyramid only (TENANT ⊃ NETWORK ⊃ {POOL, AGENT} ⊃
 * AGENT_POOL), never up. A view shows itself + its ancestors, never its
 * descendants — otherwise hundreds of rooms would dilute the network (and
 * tenant) catalog. So the network workbench shows:
 *   - ORG       (ancestor — inherited, read-only) when ``includeOrgScope``
 *   - NETWORK   (this view's own scope, matching networkId)
 *   - AGENT     (a network-workbench scope — tagged here, narrows to an agent)
 * and explicitly EXCLUDES the room scopes POOL / AGENT_POOL, which live one
 * level down in the agentic room and must not bubble up here.
 *
 * @param {object} target Normalised MountTarget-shaped object.
 * @param {string} networkId
 * @param {{ includeOrgScope?: boolean }} opts
 */
export function targetTouchesNetwork(target, networkId, opts = {}) {
  const { includeOrgScope = true } = opts;
  if (!target?.scope) return false;
  const nid = String(networkId || '').trim();
  if (!nid) return true;
  const scope = String(target.scope).toUpperCase();
  if (scope === 'ORG') return includeOrgScope;
  // Room scopes never surface in the network view (down-only rule).
  if (scope === 'POOL' || scope === 'AGENT_POOL') return false;
  // NETWORK / AGENT belong to this network when their networkId matches.
  return String(target.networkId || '').trim() === nid;
}

/**
 * Tenant catalog row is visible inside a network workbench when tagged for ORG or that network.
 * @param {object} asset
 * @param {string} networkId
 */
export function assetHasMountForNetwork(asset, networkId, opts = {}) {
  const mounts = Array.isArray(asset?.mounts) ? asset.mounts : [];
  return mounts.some((m) => {
    const t = mountRowToTarget(m);
    if (!t || !targetTouchesNetwork(t, networkId, opts)) return false;
    // Inherited ANCESTOR (ORG) only surfaces downstream when ACTIVE — a held
    // ancestor shapes nothing in this network's context. The network's OWN
    // scopes (NETWORK/AGENT) stay visible even when held (manage them here).
    if (String(t.scope).toUpperCase() === 'ORG') return inheritedMountIsActive(m);
    return true;
  });
}

/**
 * An asset's ORIGIN scope (where it was first uploaded) belongs to the NETWORK
 * workbench. A file stays visible in the scope it was created in even after
 * HOLD strips every mount, so the catalog includes it when it EITHER has a
 * mount for the scope OR its origin pins to it.
 *
 * Down-only rule (same as ``targetTouchesNetwork``): only NETWORK / AGENT
 * origins surface here. A file UPLOADED IN A ROOM (origin POOL / AGENT_POOL)
 * must NOT bubble up to its network view — that's the room's scope, one level
 * down. ORG origin is a tenant file (shown via its ORG mount, not origin).
 * @param {object} asset
 * @param {string} networkId
 */
export function assetOriginatesInNetwork(asset, networkId) {
  const nid = String(networkId || '').trim();
  if (!nid) return false;
  const scope = String(asset?.originScope || '').toUpperCase();
  // Only network-workbench scopes; room scopes (POOL/AGENT_POOL) and ORG excluded.
  if (scope !== 'NETWORK' && scope !== 'AGENT') return false;
  return String(asset?.originNetworkId || '').trim() === nid;
}

/**
 * Is an asset visible in the TENANT (workspace) catalog? Down-only: the tenant
 * is the TOP of the pyramid, so it shows ONLY tenant-level (ORG) context — a
 * file with an ORG mount, OR one uploaded at tenant scope (origin ORG). Files
 * created in a network or room (origin/mount NETWORK/POOL/AGENT/AGENT_POOL) must
 * NOT surface here — hundreds of rooms would otherwise drown the tenant view.
 * @param {object} asset
 */
export function assetVisibleAtTenant(asset) {
  const mounts = Array.isArray(asset?.mounts) ? asset.mounts : [];
  const hasOrgMount = mounts.some(
    (m) => String((mountRowToTarget(m) || {}).scope || '').toUpperCase() === 'ORG',
  );
  if (hasOrgMount) return true;
  const origin = String(asset?.originScope || '').toUpperCase();
  // Tenant upload (origin ORG) stays even when held to 0 mounts.
  if (origin === 'ORG') return true;
  // Safety net: a legacy orphan with NO origin AND NO mounts has no home scope —
  // surface it at tenant (the only place to manage/delete it) so it can't be
  // stranded invisible. A file with a known downstream origin or any mount is
  // NOT an orphan and is intentionally excluded (down-only rule).
  return !origin && mounts.length === 0;
}

/**
 * An asset's origin scope matches an agentic pool axis (POOL / AGENT_POOL on
 * this network + pool). Used so a room "Context" tab upload stays visible in
 * that room after HOLD. Supervisor narrowing is intentionally NOT applied:
 * origin visibility is about the pool the file was created in, not assignment.
 * @param {object} asset
 * @param {string} networkId
 * @param {string} poolId
 */
export function assetOriginatesInAgenticPool(asset, networkId, poolId) {
  const nid = String(networkId || '').trim();
  const pid = String(poolId || '').trim();
  if (!nid || !pid) return false;
  const scope = String(asset?.originScope || '').toUpperCase();
  if (scope !== 'POOL' && scope !== 'AGENT_POOL') return false;
  if (String(asset?.originNetworkId || '').trim() !== nid) return false;
  return String(asset?.originPoolId || '').trim() === pid;
}

/**
 * Is this row INHERITED-ONLY (display-only) at the current view scope?
 *
 * True when the row is visible here purely because of ancestor mounts — it has
 * NO own-scope mount at this level. Inherited rows show their real lane (so the
 * user sees what's shaping context) but are READ-ONLY: you manage the tag at the
 * scope it was applied. The view scope is implied by the args:
 *   - room (networkId + poolId): own = POOL / AGENT_POOL on this pool axis
 *   - network (networkId only):  own = NETWORK / AGENT on this network
 *   - tenant (neither):          nothing is inherited (ORG is its own scope)
 * @param {object} asset
 * @param {string} [networkId]
 * @param {string} [poolId]
 * @param {object} [opts] supervisorAgentIds for the agentic pyramid
 */
export function assetIsInheritedOnly(asset, networkId, poolId, opts = {}) {
  const nid = String(networkId || '').trim();
  const pid = String(poolId || '').trim();
  if (!nid) return false; // tenant scope: ORG is owned here, never "inherited"
  const mounts = Array.isArray(asset?.mounts) ? asset.mounts : [];
  const OWN = pid ? new Set(['POOL', 'AGENT_POOL']) : new Set(['NETWORK', 'AGENT']);
  const hasOwn = mounts.some((m) => {
    const t = mountRowToTarget(m);
    if (!t) return false;
    const sc = String(t.scope).toUpperCase();
    if (!OWN.has(sc)) return false;
    if (String(t.networkId || '').trim() !== nid) return false;
    if (pid && String(t.poolId || '').trim() !== pid) return false;
    return true;
  });
  if (hasOwn) return false;
  // A file ORIGINATING here (even held to 0 own mounts) is the user's OWN row,
  // not inherited — keep it editable so they can re-tag/manage it.
  if (pid ? assetOriginatesInAgenticPool(asset, nid, pid) : assetOriginatesInNetwork(asset, nid)) {
    return false;
  }
  return true; // visible only via ancestors → display-only
}

/**
 * Subset of mount rows/chips meant for Tags column inside a scoped network UI.
 */
export function mountsVisibleForNetwork(assetOrMounts, networkId, opts = {}) {
  const mounts = Array.isArray(assetOrMounts)
    ? assetOrMounts
    : Array.isArray(assetOrMounts?.mounts)
      ? assetOrMounts.mounts
      : [];
  const nid = String(networkId || '').trim();
  if (!nid) return mounts;
  return mounts.filter((m) => {
    const t = mountRowToTarget(m);
    if (!t || !targetTouchesNetwork(t, nid, opts)) return false;
    // Inherited ORG only previews downstream when active (see assetHasMountForNetwork).
    if (String(t.scope).toUpperCase() === 'ORG') return inheritedMountIsActive(m);
    return true;
  });
}

/** Non-ORG mounts visible in a network catalog (used when stripping tags on Hold). */
export function mountsRemovableForNetwork(assetOrMounts, networkId, opts = {}) {
  return mountsVisibleForNetwork(assetOrMounts, networkId, opts).filter((m) => {
    const sc = String(m?.target?.scope ?? m?.scope ?? '').toUpperCase();
    return sc && sc !== 'ORG';
  });
}

/**
 * Per-lane (rules / reference) effective usage in a network: returns ``RULES``/``REFERENCE``
 * iff at least one **non-ORG** mount visible to the network is currently in that lane.
 *
 * Use this for the catalog Hold/Rules and Hold/Reference radiogroups: rules and reference
 * are independent subscriptions on the same asset (different tags can hold each lane).
 *
 * @param {object} asset
 * @param {'rules' | 'reference'} lane
 * @param {string} networkId
 * @param {{ includeOrgScope?: boolean }} opts
 * @returns {'RULES' | 'REFERENCE' | 'OFF'}
 */
export function effectiveAssetLaneUsageForNetwork(asset, lane, networkId, opts = {}) {
  const want = lane === 'reference' ? 'REFERENCE' : 'RULES';
  // The lane is ACTIVE if ANY visible mount has this usage — including an active
  // inherited ancestor (ORG). ``mountsVisibleForNetwork`` already gates held
  // ancestors out, so a tenant @Org file tagged RULES correctly shows RULES (not
  // HOLD) downstream. (We previously skipped ORG here, which forced every
  // inherited file to render HOLD.)
  const vis = mountsVisibleForNetwork(asset, networkId, opts);
  for (const m of vis) {
    const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
    if (u === want) return want;
  }
  return 'OFF';
}

/**
 * Memory lane / usage filter for a catalog row: prefers per-mount ``usageType``
 * for mounts in the scoped network (excluding ORG-only rows when network-scoped mounts exist).
 */
export function effectiveAssetUsageForNetwork(asset, networkId, opts = {}) {
  const vis = mountsVisibleForNetwork(asset, networkId, opts);
  const rows = vis;
  if (!rows.length) {
    const d = String(asset?.usageType ?? 'OFF').toUpperCase();
    if (d === 'RULES' || d === 'REFERENCE' || d === 'OFF') return d;
    return 'OFF';
  }
  const usages = [];
  for (const m of rows) {
    const u = String(m?.usageType ?? m?.usage_type ?? '').toUpperCase();
    if (u === 'RULES' || u === 'REFERENCE' || u === 'OFF') usages.push(u);
  }
  const active = usages.filter((u) => u !== 'OFF');
  if (active.length === 0) return 'OFF';
  const uniq = [...new Set(active)];
  if (uniq.length === 1) return uniq[0];
  if (uniq.includes('RULES')) return 'RULES';
  return uniq.includes('REFERENCE') ? 'REFERENCE' : 'OFF';
}

/**
 * Build a deterministic suggestion list for the picker autocomplete given
 * the current network context. The org-scoped option is omitted for callers
 * who haven't been granted org-level write access (RBAC re-checks server-
 * side; this only shapes the menu).
 *
 * ``mountSuggestMode``:
 *   * ``full`` (default): org / net / agents / pools (Workspace + Network tabs).
 *   * ``pool_axis``: Agentic **owners** — ``@Pool`` + ``@AgentPool`` only.
 *   * ``agent_pool_only``: Agentic **supervisors** — ``@AgentPool`` cross-product only.
 *
 * @param {{
 *   networkId?: string,
 *   agentNames?: string[],
 *   poolIds?: string[],
 *   includeOrg?: boolean,
 *   mountSuggestMode?: string,
 * }} ctx
 * @returns {object[]}
 */
export function suggestTargets(ctx = {}) {
  const mode = ctx.mountSuggestMode || MOUNT_SUGGEST_FULL;
  if (
    mode === MOUNT_SUGGEST_POOL_AXIS
    || mode === MOUNT_SUGGEST_AGENT_POOL_ONLY
  ) {
    const nid = String(ctx.networkId || '').trim();
    const pools = (ctx.poolIds || []).filter(
      (p) => typeof p === 'string' && p.trim().length > 0,
    );
    const agents = (ctx.agentNames || []).filter(
      (a) => typeof a === 'string' && a.trim().length > 0,
    );
    if (!nid || !pools.length) return [];

    /** @type {object[]} */
    const out = [];
    if (mode === MOUNT_SUGGEST_POOL_AXIS) {
      for (const p of pools) {
        out.push({ scope: SCOPES.POOL, networkId: nid, poolId: p });
        for (const a of agents) {
          out.push({
            scope: SCOPES.AGENT_POOL,
            networkId: nid,
            agentName: a,
            poolId: p,
          });
        }
      }
      return out;
    }
    /** agent_pool_only */
    if (!agents.length) return [];
    for (const p of pools) {
      for (const a of agents) {
        out.push({
          scope: SCOPES.AGENT_POOL,
          networkId: nid,
          agentName: a,
          poolId: p,
        });
      }
    }
    return out;
  }

  const out = [];
  if (ctx.includeOrg) out.push({ scope: SCOPES.ORG });
  if (ctx.networkId) out.push({ scope: SCOPES.NETWORK, networkId: ctx.networkId });
  for (const a of ctx.agentNames || []) {
    out.push({ scope: SCOPES.AGENT, networkId: ctx.networkId, agentName: a });
  }
  for (const p of ctx.poolIds || []) {
    out.push({ scope: SCOPES.POOL, networkId: ctx.networkId, poolId: p });
    for (const a of ctx.agentNames || []) {
      out.push({
        scope: SCOPES.AGENT_POOL,
        networkId: ctx.networkId,
        agentName: a,
        poolId: p,
      });
    }
  }
  return out;
}
